import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Card,
  CardContent,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@betstarters/ui'
import { ArrowLeft, Ban, Pencil, Plus, RotateCcw, Search } from 'lucide-react'
import { api } from '../api'
import { useCapabilities } from '../hooks/useCapabilities'
import { useReference } from '../hooks/useReference'
import { messageOf } from '../lib/apiError'
import { ErrorBanner, StatusBadge } from '../components/catalog'
import { Combobox } from '../components/controls'
import { Tab, TabCount, Tabs, TabsList, TabsPanel } from '../components/tabs'
import {
  JurisdictionForm,
  emptyJurisdictionForm,
  type JurisdictionFormValue,
} from '../components/geoForms'
import type { Jurisdiction } from './Jurisdictions'

interface CountryLink {
  countryId: number
  country: string
  isoCode2: string
  isDisabledByPlatform: boolean
  creationDate: string
  lastModificationDate: string | null
}

const TABS = ['summary', 'countries'] as const
type TabValue = (typeof TABS)[number]

/**
 * JurisdictionDetail is one licensing jurisdiction and the countries it covers,
 * split into tabs: its own fields, and the link table.
 *
 * Unlinking a country DISABLES the link instead of deleting it — that table
 * carries IsDisabledByPlatform and LastModificationDate, so the row has a
 * lifecycle worth keeping (unlike the PSP hub's links, which are bare
 * assignments). Hence "Disabilita/Ripristina" here rather than a bin icon.
 */
export function JurisdictionDetailPage() {
  const { t } = useTranslation('app')
  const { id } = useParams()
  const qc = useQueryClient()
  const { has } = useCapabilities()
  const canWrite = has('catalog:write')
  const { countries } = useReference()

  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.get('tab') as TabValue | null
  const tab: TabValue = requested && TABS.includes(requested) ? requested : 'summary'

  const [error, setError] = useState('')
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<JurisdictionFormValue>(emptyJurisdictionForm)
  const [countrySearch, setCountrySearch] = useState('')
  const [newCountry, setNewCountry] = useState('')

  const { data: jurisdiction } = useQuery({
    queryKey: ['jurisdictions', id],
    queryFn: async (): Promise<Jurisdiction> => {
      const res = await api.get(`/jurisdictions/${id}`)
      return res.data as Jurisdiction
    },
  })

  const { data: linkData } = useQuery({
    queryKey: ['jurisdictions', id, 'countries'],
    queryFn: async (): Promise<{ countries: CountryLink[] }> => {
      const res = await api.get(`/jurisdictions/${id}/countries`)
      return res.data as { countries: CountryLink[] }
    },
  })

  useEffect(() => {
    if (jurisdiction) setForm({ jurisdiction: jurisdiction.jurisdiction })
  }, [jurisdiction])

  const invalidate = () => qc.invalidateQueries({ queryKey: ['jurisdictions'] })
  const onError = (err: unknown) => setError(messageOf(err, t('jurisdictions.errors.save')))

  const save = useMutation({
    mutationFn: () =>
      api.put(`/jurisdictions/${id}`, {
        jurisdiction: form.jurisdiction.trim(),
        isDisabledByPlatform: jurisdiction?.isDisabledByPlatform ?? false,
      }),
    onSuccess: () => {
      setEditing(false)
      setError('')
      invalidate()
    },
    onError,
  })

  const toggleDisabled = useMutation({
    mutationFn: () =>
      jurisdiction?.isDisabledByPlatform
        ? api.post(`/jurisdictions/${id}/restore`)
        : api.delete(`/jurisdictions/${id}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError,
  })

  const addCountry = useMutation({
    mutationFn: (countryId: number) => api.put(`/jurisdictions/${id}/countries/${countryId}`),
    onSuccess: () => {
      setError('')
      setNewCountry('')
      invalidate()
    },
    onError,
  })

  const toggleCountry = useMutation({
    mutationFn: (link: CountryLink) =>
      link.isDisabledByPlatform
        ? api.post(`/jurisdictions/${id}/countries/${link.countryId}/restore`)
        : api.delete(`/jurisdictions/${id}/countries/${link.countryId}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError,
  })

  const links = linkData?.countries ?? []
  const term = countrySearch.trim().toLowerCase()
  const visibleLinks = term
    ? links.filter(
        (l) => l.country.toLowerCase().includes(term) || l.isoCode2.toLowerCase().includes(term),
      )
    : links

  const linkedIds = new Set(links.map((l) => l.countryId))
  const linkableCountries = countries.filter((c) => !linkedIds.has(c.id))

  if (!jurisdiction) {
    return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
  }

  const formatDate = (value: string | null) => (value ? new Date(value).toLocaleString() : '—')

  return (
    <div>
      <Link
        to="/jurisdictions"
        className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        {t('jurisdictions.title')}
      </Link>

      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold">{jurisdiction.jurisdiction}</h2>
          <p className="text-sm text-muted-foreground">
            {t('jurisdictions.columns.id')} {jurisdiction.jurisdictionId}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge disabled={jurisdiction.isDisabledByPlatform} />
          {canWrite && (
            <Button
              variant="outline"
              size="sm"
              disabled={toggleDisabled.isPending}
              onClick={() => toggleDisabled.mutate()}
            >
              {jurisdiction.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
              {jurisdiction.isDisabledByPlatform ? t('common.restore') : t('common.retire')}
            </Button>
          )}
        </div>
      </div>

      <ErrorBanner message={error} />

      <Tabs
        value={tab}
        onValueChange={(value) => {
          searchParams.set('tab', String(value))
          setSearchParams(searchParams, { replace: true })
        }}
      >
        <TabsList>
          <Tab value="summary">{t('jurisdictions.tabs.summary')}</Tab>
          <Tab value="countries">
            {t('jurisdictions.tabs.countries')}
            <TabCount value={links.length} />
          </Tab>
        </TabsList>

        <TabsPanel value="summary">
          {editing ? (
            <JurisdictionForm
              value={form}
              onChange={setForm}
              onSubmit={() => save.mutate()}
              onCancel={() => {
                setEditing(false)
                setError('')
              }}
              pending={save.isPending}
              error={error}
              submitLabel={t('common.save')}
            />
          ) : (
            <>
              <Card className="max-w-3xl">
                <CardContent>
                  <dl className="grid grid-cols-[12rem_1fr] gap-x-6 gap-y-3 text-sm">
                    <dt className="text-muted-foreground">{t('jurisdictions.columns.id')}</dt>
                    <dd className="font-mono text-xs">{jurisdiction.jurisdictionId}</dd>
                    <dt className="text-muted-foreground">{t('jurisdictions.columns.name')}</dt>
                    <dd>{jurisdiction.jurisdiction}</dd>
                    <dt className="text-muted-foreground">{t('jurisdictions.columns.countries')}</dt>
                    <dd>
                      {t('jurisdictions.countriesValue', {
                        active: jurisdiction.activeCountryCount,
                        total: jurisdiction.countryCount,
                      })}
                    </dd>
                    <dt className="text-muted-foreground">{t('common.columns.created')}</dt>
                    <dd>{formatDate(jurisdiction.creationDate)}</dd>
                    <dt className="text-muted-foreground">
                      {t('paymentMethods.summary.lastModified')}
                    </dt>
                    <dd>{formatDate(jurisdiction.lastModificationDate)}</dd>
                  </dl>
                </CardContent>
              </Card>

              {canWrite && (
                <Button className="mt-6" variant="outline" size="sm" onClick={() => setEditing(true)}>
                  <Pencil />
                  {t('common.edit')}
                </Button>
              )}
            </>
          )}
        </TabsPanel>

        <TabsPanel value="countries">
          <div className="mb-3 flex flex-wrap items-center justify-end gap-2">
            <div className="relative">
              <Search className="absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={countrySearch}
                onChange={(e) => setCountrySearch(e.target.value)}
                placeholder={t('jurisdictions.countries.searchPlaceholder')}
                className="w-56 pl-8"
              />
            </div>
            {canWrite && (
              <>
                <Combobox
                  value={newCountry}
                  onValueChange={setNewCountry}
                  items={linkableCountries.map((c) => ({
                    value: String(c.id),
                    label: `${c.isoCode2} — ${c.name}`,
                  }))}
                  placeholder={t('jurisdictions.countries.addPlaceholder')}
                  className="w-56"
                />
                <Button
                  size="sm"
                  disabled={!newCountry || addCountry.isPending}
                  onClick={() => addCountry.mutate(Number(newCountry))}
                >
                  <Plus />
                  {t('common.add')}
                </Button>
              </>
            )}
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-20">{t('jurisdictions.countries.iso')}</TableHead>
                  <TableHead>{t('jurisdictions.countries.country')}</TableHead>
                  <TableHead className="w-32">{t('common.columns.status')}</TableHead>
                  <TableHead className="w-40">{t('common.columns.created')}</TableHead>
                  {canWrite && (
                    <TableHead className="w-28 text-right">{t('common.columns.actions')}</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleLinks.map((l) => (
                  <TableRow key={l.countryId}>
                    <TableCell className="font-mono text-xs">{l.isoCode2 || '—'}</TableCell>
                    <TableCell>{l.country || `#${l.countryId}`}</TableCell>
                    <TableCell>
                      <StatusBadge disabled={l.isDisabledByPlatform} />
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(l.creationDate).toLocaleDateString()}
                    </TableCell>
                    {canWrite && (
                      <TableCell>
                        <div className="flex justify-end">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={l.isDisabledByPlatform ? t('common.restore') : t('common.retire')}
                            disabled={toggleCountry.isPending}
                            onClick={() => toggleCountry.mutate(l)}
                          >
                            {l.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {visibleLinks.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={canWrite ? 5 : 4}
                      className="py-8 text-center text-muted-foreground"
                    >
                      {t('jurisdictions.countries.empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsPanel>
      </Tabs>
    </div>
  )
}
